const PICKUP_AVAILABILITY_QUERY = /* GraphQL */ `query PickupAvailability($ids: [ID!]!) {
  nodes(ids: $ids) {
    ... on ProductVariant {
      id
      title
      selectedOptions {
        name
        value
      }
      storeAvailability(first: 10) {
        nodes {
          available
          quantityAvailable
          location {
            name
            address {
              address1
              city
              zip
            }
          }
        }
      }
    }
  }
}`;

let latestVariantId = null;
let variantCache = new Map();
let inFlightFetch = null;

function pickupBodies() {
  return [...document.querySelectorAll('[data-pickup-availability-body]')];
}

function sizeVariantIds() {
  const ids = [...document.querySelectorAll('[data-pickup-size-option]')].map((button) => button.dataset.variantId);
  const initialId = pickupBodies()[0]?.dataset.initialVariantId;
  if (initialId && !ids.includes(initialId)) ids.push(initialId);
  return ids;
}

async function fetchVariants(config) {
  const response = await fetch(`/api/${config.apiVersion || '2026-04'}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': config.storefrontToken,
    },
    body: JSON.stringify({
      query: PICKUP_AVAILABILITY_QUERY,
      variables: { ids: sizeVariantIds().map((id) => `gid://shopify/ProductVariant/${id}`) },
    }),
  });
  const { data } = await response.json();
  const nodes = (data?.nodes ?? []).filter(Boolean);
  if (!nodes.length) {
    console.warn(
      '[pickup-availability] no variants resolved — check the product is published to the storefront token\'s app and the token belongs to this store'
    );
  }
  const hasNullQuantities = nodes.some((node) =>
    (node.storeAvailability?.nodes ?? []).some((store) => store.quantityAvailable == null)
  );
  if (hasNullQuantities) {
    console.warn(
      '[pickup-availability] quantityAvailable is null — stock threshold disabled; check the token\'s app has the unauthenticated_read_product_inventory scope'
    );
  }
  variantCache = new Map(nodes.map((node) => [node.id.split('/').pop(), node]));
}

async function ensureVariants(config) {
  if (inFlightFetch) {
    await inFlightFetch;
    return;
  }
  inFlightFetch = fetchVariants(config);
  try {
    await inFlightFetch;
  } finally {
    inFlightFetch = null;
  }
}

async function refreshPickupAvailability(variantId) {
  const bodies = pickupBodies();
  if (!bodies.length) return;

  const { storefrontToken, apiVersion, stockThreshold } = bodies[0].dataset;
  if (!storefrontToken) return;

  latestVariantId = variantId;
  try {
    if (!variantCache.has(variantId)) {
      await ensureVariants({ storefrontToken, apiVersion });
      const supersededByNewerSelection = latestVariantId !== variantId;
      if (supersededByNewerSelection) return;
    }

    const variant = variantCache.get(variantId);
    if (!variant) {
      document.querySelectorAll('[data-pickup-availability-row]').forEach((row) => row.classList.add('hidden'));
      return;
    }

    const stores = variant.storeAvailability?.nodes ?? [];
    const threshold = Number(stockThreshold) || 10;

    pickupBodies().forEach((body) => renderStores(body, variant, stores, threshold));
    const anyVariantHasStores = [...variantCache.values()].some(
      (cached) => (cached.storeAvailability?.nodes ?? []).length > 0
    );
    document.querySelectorAll('[data-pickup-availability-row]').forEach((row) => {
      row.classList.toggle('hidden', !anyVariantHasStores);
    });
    const anyStoreInStock = stores.some((store) => storeInStock(store, threshold));
    document.querySelectorAll('[data-pickup-add-to-cart]').forEach((button) => {
      button.classList.toggle('hidden', !anyStoreInStock);
      button.classList.toggle('flex', anyStoreInStock);
    });
  } catch (error) {
    console.error(error);
  }
}

function renderStores(body, variant, stores, threshold) {
  const list = body.querySelector('[data-pickup-availability-stores]');
  const template = body.querySelector('[data-pickup-store-template]');
  if (!list || !template) return;

  list.replaceChildren(
    ...stores.map((store) => {
      const item = template.content.cloneNode(true);
      item.querySelector('[data-store-name]').textContent = store.location.name;
      item.querySelector('[data-store-address]').textContent = formatAddress(store.location.address);
      item.querySelector('[data-store-size]').textContent = variantSize(variant);
      const inStock = storeInStock(store, threshold);
      item.querySelector(inStock ? '[data-store-out-of-stock]' : '[data-store-in-stock]').remove();
      return item;
    })
  );
  body.querySelector('[data-pickup-availability-note]')?.classList.toggle('hidden', stores.length === 0);
  body.querySelector('[data-pickup-no-availability]')?.classList.toggle('hidden', stores.length > 0);
}

function storeInStock(store, threshold) {
  const meetsThreshold = store.quantityAvailable == null || store.quantityAvailable >= threshold;
  return store.available && meetsThreshold;
}

function variantSize(variant) {
  const variantId = variant.id.split('/').pop();
  const selectedButton = document.querySelector(`[data-pickup-size-option][data-variant-id="${variantId}"]`);
  const buttonLabel = selectedButton?.textContent.trim();
  if (buttonLabel) return buttonLabel;

  const sizeOptionName = window.pdpStrings?.options?.size?.toLowerCase();
  const sizeOption = (variant.selectedOptions ?? []).find((option) => {
    const name = option.name.toLowerCase();
    return name === 'size' || name === sizeOptionName;
  });
  return sizeOption?.value || variant.title;
}

function formatAddress(address) {
  const { address1, city, zip } = address ?? {};
  return [address1, city, zip].filter(Boolean).join(', ');
}

function setSelectedSizeOption(variantId) {
  document.querySelectorAll('[data-pickup-size-option]').forEach((button) => {
    const isSelected = button.dataset.variantId === String(variantId);
    button.classList.toggle('selected', isSelected);
    button.classList.toggle('user-selected', isSelected);
  });
}

function pdpSizeButton(variantId) {
  return document.querySelector(
    `.variant-options-wrapper .option-selector button[data-variant-id="${variantId}"]:not([data-pickup-size-option])`
  );
}

function rebuildSizeOptions(product, selectedVariantId) {
  const grids = [...document.querySelectorAll('[data-pickup-size-options]')];
  if (!grids.length || !Array.isArray(product?.variants)) return;

  const localizedSizeName = window.pdpStrings?.options?.size?.toLowerCase();
  const optionNames = (product.options ?? []).map((name) => String(name).toLowerCase());
  let sizeIndex = optionNames.findIndex((name) => name === 'size' || name === localizedSizeName);
  if (sizeIndex === -1) sizeIndex = 0;

  const selected =
    product.variants.find((variant) => String(variant.id) === String(selectedVariantId)) ?? product.variants[0];
  if (!selected) return;

  pickupBodies().forEach((body) => {
    body.dataset.initialVariantId = String(selected.id);
  });

  const sizeVariants = product.variants.filter((variant) =>
    (variant.options ?? []).every((value, index) => index === sizeIndex || value === selected.options?.[index])
  );

  grids.forEach((grid) => {
    grid.replaceChildren(
      ...sizeVariants.map((variant) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('data-pickup-size-option', '');
        button.dataset.variantId = String(variant.id);
        button.className = 'box sizeSelector flex relative justify-center items-center text-xs';
        if (String(variant.id) === String(selected.id)) button.classList.add('selected', 'user-selected');
        if (variant.available === false) button.classList.add('disabled');

        const title = document.createElement('span');
        title.className = 'button-title relative z-20 inline-block font-light';
        if (product.low_stock) title.classList.add('low-stock-dot');
        const rawSize = String(variant.options?.[sizeIndex] ?? variant.title);
        title.textContent =
          typeof window.formatVariantSize === 'function'
            ? window.formatVariantSize(rawSize, product.size_display_strategy_handle)
            : rawSize;

        button.appendChild(title);
        return button;
      })
    );
  });
}

document.addEventListener('click', (event) => {
  const addToCart = event.target.closest?.('[data-pickup-add-to-cart]');
  if (addToCart) {
    if (addToCart.dataset.loading) return;
    const pdpAddToCart = document.querySelector(
      'form[action$="/cart/add"] .add-to-cart-wrapper button[type="submit"].js-add-product'
    );
    if (!pdpAddToCart || pdpAddToCart.disabled) return;

    const defaultLabel = addToCart.textContent;
    addToCart.dataset.loading = 'true';
    addToCart.textContent = window.pdpStrings?.action?.addingToCart;

    const reset = () => {
      delete addToCart.dataset.loading;
      addToCart.textContent = defaultLabel;
    };
    const listener = new AbortController();
    const timeout = setTimeout(() => {
      listener.abort();
      reset();
    }, 10000);
    window.addEventListener(
      'cart-updated',
      () => {
        clearTimeout(timeout);
        reset();
        addToCart.closest('slide-drawer')?.hide();
      },
      { once: true, signal: listener.signal }
    );

    pdpAddToCart.click();
    return;
  }

  const button = event.target.closest?.('[data-pickup-size-option]');
  if (!button) return;

  const variantId = button.dataset.variantId;
  const pdpButton = pdpSizeButton(variantId);
  if (pdpButton) {
    pdpButton.click();
    return;
  }
  setSelectedSizeOption(variantId);
  refreshPickupAvailability(variantId);
});

window.addEventListener('variant:change', (event) => {
  const variantId = event.detail?.variantId;
  if (!variantId) return;
  setSelectedSizeOption(String(variantId));
  refreshPickupAvailability(String(variantId));
});

window.addEventListener('product:change', (event) => {
  const { product, selectedVariant } = event.detail ?? {};
  if (product) {
    rebuildSizeOptions(product, selectedVariant?.id);
    variantCache = new Map();
  }
  if (selectedVariant?.id) refreshPickupAvailability(String(selectedVariant.id));
});

const initialVariantId = pickupBodies()[0]?.dataset.initialVariantId;
if (initialVariantId) refreshPickupAvailability(initialVariantId);
